import { Extension } from "@tiptap/core";
import { Fragment, type Node } from "@tiptap/pm/model";
import { Plugin, TextSelection } from "@tiptap/pm/state";

type ListItemParagraph = {
    node: Node;
    /** Position right before the paragraph in the document it was found in */
    from: number;
};

const isHardBreak = (node: Node | null | undefined) =>
    node?.type.name === "hardBreak" || node?.type.name === "hard_break";

/**
 * Every bullet point holds one paragraph. Pasting or deleting can leave a
 * list item with more than one, so the later ones are merged into the first.
 */
const SingleParagraph = Extension.create({
    name: "noSecondParagraph",

    addProseMirrorPlugins() {
        return [
            new Plugin({
                appendTransaction: (_transactions, _oldState, newState) => {
                    const listItems: ListItemParagraph[][] = [];

                    newState.doc.descendants((node, pos) => {
                        if (node.type.name !== "listItem") {
                            return true;
                        }

                        const paragraphs: ListItemParagraph[] = [];

                        node.forEach((child, offset) => {
                            if (child.type.name === "paragraph") {
                                // The content of the list item starts one position after it
                                paragraphs.push({
                                    node: child,
                                    from: pos + 1 + offset,
                                });
                            }
                        });

                        if (paragraphs.length > 1) {
                            listItems.push(paragraphs);
                        }

                        // Nested lists can hold list items with several paragraphs too
                        return true;
                    });

                    if (listItems.length === 0) {
                        return null;
                    }

                    // All positions are taken from newState.doc, so every one is
                    // mapped through the steps added so far before it is used.
                    const tr = newState.tr;
                    // The caret goes behind the merged text, counted in the document after step caretStep
                    let caret: number | null = null;
                    let caretStep = 0;

                    for (const [first, ...later] of listItems) {
                        for (const paragraph of later) {
                            tr.delete(
                                tr.mapping.map(paragraph.from),
                                tr.mapping.map(
                                    paragraph.from + paragraph.node.nodeSize,
                                ),
                            );
                        }

                        const firstContentStart = tr.mapping.map(
                            first.from + 1,
                        );

                        // A first paragraph holding just a line break is dropped in favour of the merged text
                        if (
                            first.node.childCount === 1 &&
                            isHardBreak(first.node.firstChild)
                        ) {
                            tr.delete(
                                firstContentStart,
                                firstContentStart +
                                    (first.node.firstChild?.nodeSize ?? 0),
                            );
                        }

                        let insertPos = tr.mapping.map(
                            first.from + first.node.nodeSize - 1,
                            -1,
                        );

                        for (const paragraph of later) {
                            if (paragraph.node.content.size === 0) {
                                continue;
                            }

                            // Keep the words of both paragraphs apart
                            const textBefore = tr.doc.textBetween(
                                firstContentStart,
                                insertPos,
                            );
                            const content =
                                textBefore.length > 0 &&
                                !/\s$/.test(textBefore) &&
                                !/^\s/.test(paragraph.node.textContent)
                                    ? Fragment.from(
                                          newState.schema.text(" "),
                                      ).append(paragraph.node.content)
                                    : paragraph.node.content;

                            tr.insert(insertPos, content);
                            insertPos += content.size;
                            caret = insertPos;
                            caretStep = tr.steps.length;
                        }
                    }

                    if (caret !== null) {
                        tr.setSelection(
                            TextSelection.create(
                                tr.doc,
                                tr.mapping.slice(caretStep).map(caret),
                            ),
                        );
                    }

                    return tr.docChanged ? tr : null;
                },
            }),
        ];
    },
});

export { SingleParagraph };
